import { useEffect, useRef, useState } from 'react';
import { barcode, decodeProfile } from '../../platform/barcode';
import { openRearCameraStream } from '../../platform/camera';
import { t } from '../../platform/lang';
import { reportError } from '../../core/telemetry/report';
import { crumb } from '../../core/telemetry/session';

/**
 * Barcode scan.
 *
 * Reported after a real run: "the camera opened but it never scanned, it was
 * just like a camera." That is the worst shape a failure can take — the screen
 * looks alive, so the driver keeps trying, and nothing anywhere says it is
 * broken. Every layer below here swallowed its errors: a decode failure was
 * treated as "keep looking", so a decoder that could never work looked exactly
 * like a label held at a bad angle.
 *
 * So this component now reports its own state out loud:
 *
 *   starting  - the camera has not delivered a frame yet
 *   looking   - frames are arriving and being examined
 *   noLuck    - twenty seconds of real attempts with nothing found: advice
 *   broken    - the decoder itself failed: say so, and offer typing
 *
 * The scan is LOG-ONLY either way. A ripped hospital label must never stop a
 * legitimate delivery, so manual entry and "no barcode" are always one tap away
 * and always visible.
 */
export type BarcodeException = 'no_barcode_on_package' | 'unreadable' | 'other';

const NO_CODE_REASONS: Array<{ value: BarcodeException; key: string }> = [
  { value: 'no_barcode_on_package', key: 'scanReasonNone' },
  { value: 'unreadable',            key: 'scanReasonTorn' },
  { value: 'other',                 key: 'scanReasonOther' },
];

/*
 * A 'caught' state was added on 15 Sep to freeze the frame green on a
 * successful read — the owner could not tell one scan from five, which is a
 * real complaint and still worth fixing. It was reverted the same morning
 * because the camera came up BLACK on that build, on both this screen and the
 * photo screen, and nothing in it threw so nothing was reported.
 *
 * The cause was never found. Note in this file already warns that restyling
 * the viewfinder caused a black camera on Safari once before, so this area is
 * known-fragile: whoever tries again should do it ALONE, with a real phone to
 * test on, and not bundled with anything else.
 */
type ScanState = 'starting' | 'looking' | 'noLuck' | 'broken';

/*
 * A typed tote number saved the way the scanner reads it (10 Oct). Tote labels
 * are six digits ("000319"); drivers typing by hand often enter "319", and the
 * same tote then looked like two different totes. Only short all-digit entries
 * are padded; anything else is kept exactly as typed.
 */
function asScanned(typed: string): string {
  const v = typed.trim();
  return /^\d{1,5}$/.test(v) ? v.padStart(6, '0') : v;
}

export function BarcodeScanner(props: {
  /** A scanned/typed code, or the reason there isn't one. Never silently null. */
  onScanned: (value: string | null, exception: BarcodeException | null) => void;
  /** Called when this component has let go of the camera. */
  onReleased?: () => void;
  /**
   * Open the camera immediately instead of asking first.
   *
   * Only for the SECOND tote onwards. The driver has already answered "camera
   * or typing" for this stop and has just pressed "Scan another tote" — asking
   * the same question again would be a tap for nothing. The first scan still
   * asks, because opening a camera unbidden is the heaviest thing this app
   * does.
   */
  autoStart?: boolean;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [manual, setManual] = useState('');
  const [noCode, setNoCode] = useState(false);
  /*
   * Starts closed. Opening a camera is the heaviest thing this app does and on
   * an older phone it is what has been killing the tab, so it happens only when
   * the driver asks for it.
   */
  const [scanning, setScanning] = useState(Boolean(props.autoStart));
  /** Has the driver chosen yet? Until then, neither path is assumed. */
  const [chosen, setChosen] = useState(Boolean(props.autoStart));
  const [state, setState] = useState<ScanState>('starting');
  /** The last code accepted, shown back so the driver knows it landed. */
  const [justAdded, setJustAdded] = useState<string | null>(null);
  const [flash, setFlash] = useState(false);
  const [count, setCount] = useState(0);

  /*
   * The callback, held in a ref. The scan loop runs for the whole life of the
   * camera and must always reach the current handler; putting the prop in the
   * effect's dependencies would tear the camera down on every render, which is
   * the bug that made a second box impossible to scan.
   */
  const onScannedRef = useRef(props.onScanned);
  onScannedRef.current = props.onScanned;

  /*
   * The live stream, so it can be released deliberately rather than only on
   * unmount. An old iPhone does not hand the camera back instantly, and the
   * photo step opening a second one before the first let go froze the
   * viewfinder completely — the owner had to kill Safari.
   */
  const streamRef = useRef<MediaStream | null>(null);
  const release = () => {
    streamRef.current?.getTracks().forEach((tr) => tr.stop());
    streamRef.current = null;
    const v = videoRef.current;
    if (v) v.srcObject = null;
    props.onReleased?.();
  };

  useEffect(() => {
    if (!barcode.supported || !scanning) return;
    const abort = new AbortController();
    let stream: MediaStream | null = null;
    let timers: Array<ReturnType<typeof setTimeout>> = [];

    (async () => {
      try {
        stream = await openRearCameraStream();
        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;

        /*
         * Safari will not start a stream attached to a freshly created element
         * from the `autoPlay` attribute alone, and the decoder then waits for a
         * frame that never arrives. Same line, same reason, as SelfieCamera.
         */
        await video.play();

        /*
         * Prove the decoder can actually load BEFORE showing a live camera.
         * It is a WebAssembly module on iPhones, and if it cannot start there
         * is no point pointing a phone at anything. Previously this failure was
         * indistinguishable from a bad angle.
         */
        await barcode.ready();
        if (abort.signal.aborted) return;
        setState('looking');
        /*
         * TIME TO THE FIRST BARCODE -- not how long the camera stayed open.
         *
         * The first version timed the whole viewfinder session, which sounds
         * like the same thing and is not. A driver scanning five totes keeps
         * the camera open for half a minute quite normally, so the very first
         * real test produced a 26-second 'slow' report for a scan that the
         * owner described as instant. Nine drivers doing that at 14:00 would
         * have filed a slow report every single pickup, and a warning that
         * fires every time is worse than no warning at all.
         *
         * What anybody actually means by "the scanner is slow" is how long they
         * stood there before the FIRST one read. Eight seconds of that is a
         * genuine complaint; thirty seconds of scanning five boxes is a driver
         * doing their job.
         */
        const openedAt = Date.now();
        let firstHit = true;

        // Real attempts are happening; if none of them find anything after a
        // while, that is worth saying rather than leaving them guessing.
        timers.push(setTimeout(() => {
          setState((s) => (s === 'looking' ? 'noLuck' : s));
        }, 20_000));

        let last = '';
        let lastAt = 0;
        while (!abort.signal.aborted) {
          const value = await barcode.scanFromVideo(video, abort.signal);
          const now = Date.now();
          /*
           * One label stays in frame for a second or two after it is read.
           *
           * This `continue` re-enters the decoder immediately, so it is the
           * hottest path in the app -- which is why nothing expensive may live
           * in this loop. A per-scan timing wrapper was briefly here and had to
           * come out: it ran on every rejected duplicate too.
           */
          if (value === last && now - lastAt < 2000) continue;
          last = value;
          lastAt = now;
          setState('looking');

          /*
           * Buzz, flash, and say so. A supermarket scanner beeps because a
           * person holding a box cannot watch a screen; the same is true of a
           * driver with a tote under one arm.
           */
          if (firstHit) {
            firstHit = false;
            const waited = Date.now() - openedAt;
            if (waited >= 8_000) {
              reportError('slow', 'first-scan', `${waited}ms ${decodeProfile()}`);
            }
          }

          try { navigator.vibrate?.(60); } catch { /* not on iOS, no matter */ }
          setFlash(true);
          setTimeout(() => setFlash(false), 220);
          setJustAdded(value);
          setCount((n) => n + 1);

          onScannedRef.current(value, null);
        }
      } catch (err) {
        if (abort.signal.aborted) return;
        // A camera the driver refused, or a decoder that cannot start. Either
        // way: say so, and put them straight into typing.
        console.error('[scan]', (err as Error)?.message);
        /*
         * The office needs to know this happened without the driver having to
         * phone in. "The scanner does not work" becomes a timestamped line
         * naming the phone, which is the difference between three days of
         * guessing and one afternoon.
         */
        reportError(
          (err as Error)?.name === 'NotAllowedError' ? 'camera_denied'
            : 'decoder_failed',
          'scan', err);
        setState('broken');
        setScanning(false);
      }
    })();

    return () => {
      /*
       * What this phone measured itself at, left in the trail. Costs nothing
       * and means every later report carries the decode cost of the device
       * that produced it.
       */
      crumb(decodeProfile());
      abort.abort();
      timers.forEach(clearTimeout);
      timers = [];
      stream?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      const v = videoRef.current;
      if (v) v.srcObject = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanning]);

  if (!chosen && !scanning) {
    return (
      <div className="barcode-scanner">
        <p className="hint">{t('scanHowTitle')}</p>
        <div className="col" style={{ gap: 10 }}>
          {barcode.supported && (
            <button className="btn primary big"
                    onClick={() => { setChosen(true); setScanning(true); }}>
              {t('scanWithCamera')}
            </button>
          )}
          <button className="btn secondary big" onClick={() => setChosen(true)}>
            {t('scanTypeIt')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="barcode-scanner">
      {scanning ? (
        <>
          {justAdded && (
            <div className="banner ok-banner scan-added">
              <strong>{t('scanAdded')}</strong>
              <span className="mono">{justAdded}</span>
            </div>
          )}
          <p className="hint">{t('scanHold')}</p>
          <div className="viewfinder-wrap">
            <video ref={videoRef} autoPlay playsInline muted className="viewfinder" />
            {/* The band the decoder actually reads. Drawn as an overlay so the
                video element is never touched — restyling it is what caused the
                black-camera bug on Safari. */}
            <div className="scan-frame" aria-hidden="true">
              <span className="c tl" /><span className="c tr" />
              <span className="c bl" /><span className="c br" />
              {/* A moving line, like the scanner at a supermarket till. A still
                  frame gives no sign the app is doing anything, so a driver
                  cannot tell "keep holding" from "it has stopped working". */}
              {state === 'looking' && !flash && <span className="sweep" />}
            </div>
            {flash && <div className="scan-flash" />}
          </div>
          <p className="scan-status">
            {state === 'starting' ? t('scanStarting')
              : state === 'noLuck' ? t('scanNoLuck')
              : t('scanLooking')}
          </p>
          <button className="btn secondary" onClick={() => setScanning(false)}>
            {t('scanManualInstead')}
          </button>
        </>
      ) : (
        <div className="col">
          {state === 'broken' && (
            <div className="banner warn-banner">{t('scanBroken')}</div>
          )}
          <input
            className="input"
            inputMode="text"
            autoFocus
            placeholder={t('scanFieldLabel')}
            value={manual}
            onChange={(e) => setManual(e.target.value)}
          />
          <div className="row" style={{ gap: 8 }}>
            {barcode.supported && state !== 'broken' && (
              <button className="btn secondary" onClick={() => setScanning(true)}>
                {t('scanWithCamera')}
              </button>
            )}
            <button className="btn primary" disabled={!manual.trim()}
                    onClick={() => { props.onScanned(asScanned(manual), null); setManual(''); }}>
              {t('scanAddCode')}
            </button>
          </div>

          {/*
           * Some packages genuinely have no barcode — a plain cardboard box — and
           * that is a fact about the delivery, not a driver failing to scan. It
           * is recorded as a reason rather than left as an empty field.
           */}
          {!noCode ? (
            <button className="btn link" onClick={() => setNoCode(true)}>
              {t('scanNoneOnThis')}
            </button>
          ) : (
            <div className="col">
              {NO_CODE_REASONS.map((r) => (
                <button key={r.value} className="btn secondary"
                        onClick={() => props.onScanned(null, r.value)}>
                  {t(r.key as Parameters<typeof t>[0])}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
